/* Super Admin — Commercial Package Builder + AI Credit cost settings. */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Package, Plus, Pencil, Trash2, X, Loader2, Sparkles,
  Users, HardDrive, CalendarClock, Save, RotateCcw, CheckCircle2, EyeOff,
} from "lucide-react";

const inp = "w-full px-3 py-2 border border-neutral-300 text-sm focus:ring-1 focus:ring-[#002FA7] focus:border-[#002FA7] outline-none rounded-md";

const EMPTY = {
  code: "", name: "", price_monthly: 0, price_yearly: 0,
  student_limit: 25, ai_credits: 1000, storage_mb: 5000, class_limit: 200,
  features: [], is_active: true,
};

export default function AdminPackages() {
  const [plans, setPlans] = useState(null);
  const [editing, setEditing] = useState(null); // plan object being edited/created
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/plans");
      setPlans(data.plans || []);
    } catch { toast.error("Could not load plans"); }
  };
  useEffect(() => { load(); }, []);

  const savePlan = async () => {
    if (!editing.code?.trim() || !editing.name?.trim()) return toast.error("Code and name are required");
    setSaving(true);
    try {
      await api.post("/admin/plans", editing);
      toast.success(`Plan "${editing.name}" saved`);
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const delPlan = async (p) => {
    if (!window.confirm(`Delete the "${p.name}" plan? Existing subscribers keep their current limits.`)) return;
    try {
      await api.delete(`/admin/plans/${p.code}`);
      toast.success("Plan deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-10" data-testid="admin-packages">
      <div>
        <Link to="/admin" className="text-[11px] text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1 mb-1"><ArrowLeft size={11} /> Admin dashboard</Link>
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-500 mb-1">/ admin · monetisation</div>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <h1 className="font-display font-bold text-3xl tracking-tighter inline-flex items-center gap-2"><Package size={26} /> Package Builder</h1>
          <button
            onClick={() => setEditing({ ...EMPTY })}
            data-testid="new-plan-btn"
            className="text-xs font-bold bg-[#0A0C10] text-white px-4 py-2.5 rounded-md hover:bg-black inline-flex items-center gap-1.5"
          ><Plus size={13} /> New plan</button>
        </div>
        <p className="text-sm text-neutral-500 mt-1">Changes go live instantly on the pricing page and checkout.</p>
      </div>

      {!plans ? (
        <div className="text-neutral-500 text-sm inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading plans…</div>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {plans.map((p) => (
            <div key={p.code} data-testid={`plan-card-${p.code}`} className={`bg-white border rounded-xl p-5 relative ${p.is_active === false ? "border-neutral-200 opacity-60" : "border-black/10"}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-display font-bold text-lg tracking-tight">{p.name}</div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-neutral-400">{p.code}{p.audience ? ` · ${p.audience}` : ""}</div>
                </div>
                <div className="flex items-center gap-1">
                  {p.is_active === false && <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-500 inline-flex items-center gap-1"><EyeOff size={9} /> hidden</span>}
                  <button onClick={() => setEditing({ ...EMPTY, ...p })} data-testid={`plan-edit-${p.code}`} className="p-1.5 text-neutral-400 hover:text-[#0A0C10] rounded hover:bg-black/5"><Pencil size={13} /></button>
                  {p.code !== "trial" && (
                    <button onClick={() => delPlan(p)} data-testid={`plan-del-${p.code}`} className="p-1.5 text-neutral-400 hover:text-red-600 rounded hover:bg-red-50"><Trash2 size={13} /></button>
                  )}
                </div>
              </div>
              <div className="mt-3 flex items-baseline gap-2">
                <span className="text-3xl font-display font-bold">${Number(p.price_monthly).toFixed(p.price_monthly % 1 ? 2 : 0)}</span>
                <span className="text-xs text-neutral-500">/month · ${Number(p.price_yearly).toFixed(0)}/yr</span>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-[11.5px] text-neutral-600">
                <span className="inline-flex items-center gap-1.5"><Users size={11} /> {p.student_limit} students</span>
                <span className="inline-flex items-center gap-1.5"><Sparkles size={11} /> {p.ai_credits} credits</span>
                <span className="inline-flex items-center gap-1.5"><HardDrive size={11} /> {(p.storage_mb / 1000).toFixed(1)} GB</span>
                <span className="inline-flex items-center gap-1.5"><CalendarClock size={11} /> {p.class_limit} classes</span>
              </div>
              {(p.features || []).length > 0 && (
                <ul className="mt-3 space-y-1">
                  {p.features.slice(0, 5).map((f, i) => (
                    <li key={i} className="text-[11.5px] text-neutral-500 inline-flex items-start gap-1.5 w-full"><CheckCircle2 size={11} className="text-emerald-500 mt-0.5 shrink-0" /> {f}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      <CreditCostsSection />

      <PurchasePricingSection />

      {editing && (
        <PlanModal editing={editing} setEditing={setEditing} onSave={savePlan} saving={saving} />
      )}
    </div>
  );
}

function PlanModal({ editing, setEditing, onSave, saving }) {
  const set = (k) => (e) => setEditing({ ...editing, [k]: e.target.value });
  const num = (k) => (e) => setEditing({ ...editing, [k]: e.target.value === "" ? "" : Number(e.target.value) });
  return (
    <div className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-center justify-center px-4" onClick={(e) => { if (e.target === e.currentTarget) setEditing(null); }}>
      <div className="w-full max-w-lg bg-white rounded-xl border border-black/10 shadow-2xl max-h-[88vh] overflow-y-auto" data-testid="plan-modal">
        <div className="px-5 py-4 border-b border-neutral-100 flex items-center justify-between">
          <div className="font-display font-bold text-lg">{editing.created_at || editing.updated_at ? `Edit ${editing.name || "plan"}` : "New plan"}</div>
          <button onClick={() => setEditing(null)} className="text-neutral-400 hover:text-black"><X size={16} /></button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-neutral-500">Code (slug)</label>
            <input className={inp} value={editing.code} onChange={set("code")} placeholder="e.g. premium" data-testid="plan-code" />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-neutral-500">Display name</label>
            <input className={inp} value={editing.name} onChange={set("name")} placeholder="e.g. Premium" data-testid="plan-name" />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-neutral-500">Price / month ($)</label>
            <input type="number" min="0" step="0.01" className={inp} value={editing.price_monthly} onChange={num("price_monthly")} data-testid="plan-price-monthly" />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-neutral-500">Price / year ($)</label>
            <input type="number" min="0" step="0.01" className={inp} value={editing.price_yearly} onChange={num("price_yearly")} data-testid="plan-price-yearly" />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-neutral-500">Student limit</label>
            <input type="number" min="0" className={inp} value={editing.student_limit} onChange={num("student_limit")} data-testid="plan-students" />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-neutral-500">AI credits included</label>
            <input type="number" min="0" className={inp} value={editing.ai_credits} onChange={num("ai_credits")} data-testid="plan-credits" />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-neutral-500">Storage (MB)</label>
            <input type="number" min="0" className={inp} value={editing.storage_mb} onChange={num("storage_mb")} data-testid="plan-storage" />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-neutral-500">Class limit</label>
            <input type="number" min="0" className={inp} value={editing.class_limit} onChange={num("class_limit")} data-testid="plan-classes" />
          </div>
          <div className="col-span-2">
            <label className="text-[10px] font-mono uppercase tracking-wider text-neutral-500">Features — one per line</label>
            <textarea rows={4} className={inp} value={(editing.features || []).join("\n")}
              onChange={(e) => setEditing({ ...editing, features: e.target.value.split("\n") })}
              placeholder={"AI quiz generator\nLive classes\nPriority support"} data-testid="plan-features" />
          </div>
          <label className="col-span-2 flex items-center gap-2 text-sm">
            <input type="checkbox" checked={editing.is_active !== false}
              onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} data-testid="plan-active" />
            Visible on pricing page
          </label>
        </div>
        <div className="px-5 py-4 border-t border-neutral-100 flex justify-end gap-2">
          <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm border border-neutral-300 rounded-md hover:border-black">Cancel</button>
          <button onClick={onSave} disabled={saving} data-testid="plan-save-btn"
            className="px-5 py-2 text-sm font-bold bg-[#0A0C10] text-white rounded-md hover:bg-black disabled:opacity-60 inline-flex items-center gap-1.5">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save plan
          </button>
        </div>
      </div>
    </div>
  );
}

const COST_LABELS = {
  chat: "AI chat message", quiz: "Quiz", quiz_interactive: "Interactive quiz",
  lesson_plan: "Lesson plan", image: "AI image / diagram", paper_check: "Paper check (text)",
  paper_check_vision: "Paper check (photo)", demo_guide: "Demo guide", post_class: "Post-class pack",
  game: "Learning game", homework: "Homework set", quiz_from_image: "Quiz from image",
  story: "Animated story", smart_cards: "Smart card deck", millionaire: "Millionaire game",
  slides: "Slide deck", slides_edit: "Slide edit", ocr: "OCR / snapshot",
};

// Disk Space packages + per-seat pricing — what teachers pay when they hit
// their storage/seat limits (header "+" buttons).
function PurchasePricingSection() {
  const [opts, setOpts] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/purchase-options").then(({ data }) => setOpts(data)).catch(() => setOpts({ storage_packages: [], seat_price: 1 }));
  }, []);

  const setPkg = (i, field, value) => setOpts((o) => ({
    ...o,
    storage_packages: o.storage_packages.map((p, k) => (k === i ? { ...p, [field]: value } : p)),
  }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        storage_packages: opts.storage_packages.map((p) => ({ ...p, mb: Number(p.mb), price: Number(p.price) })),
        seat_price: Number(opts.seat_price),
      };
      await api.patch("/admin/purchase-options", payload);
      toast.success("Pricing updated — live immediately");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="purchase-pricing-section">
      <h2 className="font-display font-bold text-xl tracking-tighter inline-flex items-center gap-2"><HardDrive size={18} /> Disk Space & Seat pricing</h2>
      <p className="text-sm text-neutral-500 mt-1">What teachers pay via the header "+" buttons. Changes apply instantly.</p>
      {!opts ? (
        <div className="text-neutral-500 text-sm mt-4 inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>
      ) : (
        <div className="mt-4 bg-white border border-black/10 rounded-xl p-5 max-w-2xl space-y-4">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-neutral-500 mb-2">Storage packages</div>
            <div className="space-y-2">
              {opts.storage_packages.map((p, i) => (
                <div key={p.id} className="flex items-center gap-3" data-testid={`storage-pricing-${p.id}`}>
                  <span className="text-sm font-semibold w-20">{p.label}</span>
                  <span className="text-[10px] font-mono text-neutral-400 w-20">{p.mb} MB</span>
                  <div className="flex items-center gap-1 ml-auto">
                    <span className="text-xs text-neutral-500">$</span>
                    <input
                      type="number" min={0} step={0.5} value={p.price}
                      onChange={(e) => setPkg(i, "price", e.target.value)}
                      data-testid={`storage-price-${p.id}`}
                      className="w-24 border border-neutral-200 focus:border-[#0A0C10] rounded-md px-2 py-1.5 text-sm outline-none"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="border-t border-neutral-100 pt-4 flex items-center gap-3">
            <span className="text-sm font-semibold">Per student seat</span>
            <div className="flex items-center gap-1 ml-auto">
              <span className="text-xs text-neutral-500">$</span>
              <input
                type="number" min={0} step={0.25} value={opts.seat_price}
                onChange={(e) => setOpts((o) => ({ ...o, seat_price: e.target.value }))}
                data-testid="seat-price-input"
                className="w-24 border border-neutral-200 focus:border-[#0A0C10] rounded-md px-2 py-1.5 text-sm outline-none"
              />
              <span className="text-xs text-neutral-500">/seat</span>
            </div>
          </div>
          <button onClick={save} disabled={saving} data-testid="save-purchase-pricing"
            className="text-xs font-bold bg-[#0A0C10] text-white px-4 py-2.5 rounded-md hover:bg-black disabled:opacity-60 inline-flex items-center gap-1.5">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Save pricing
          </button>
        </div>
      )}
    </div>
  );
}

function CreditCostsSection() {
  const [costs, setCosts] = useState(null);
  const [defaults, setDefaults] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/credit-costs");
      setCosts(data.costs);
      setDefaults(data.defaults);
    } catch { toast.error("Could not load credit costs"); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/credit-costs", { costs });
      setCosts(data.costs);
      toast.success("Credit costs updated — applies to the next generation");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  if (!costs) return null;
  const dirty = Object.keys(costs).some((k) => costs[k] !== defaults[k]);

  return (
    <div data-testid="credit-costs-section">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500 mb-1">/ AI credit management</div>
          <h2 className="font-display font-bold text-xl tracking-tight inline-flex items-center gap-2"><Sparkles size={18} /> Cost per AI action</h2>
          <p className="text-sm text-neutral-500 mt-0.5">How many credits each generation deducts from a teacher's balance.</p>
        </div>
        <div className="flex items-center gap-2">
          {dirty && (
            <button onClick={() => setCosts({ ...defaults })} data-testid="credit-costs-reset"
              className="text-xs px-3 py-2 border border-neutral-300 rounded-md hover:border-black inline-flex items-center gap-1.5">
              <RotateCcw size={12} /> Reset to defaults
            </button>
          )}
          <button onClick={save} disabled={saving} data-testid="credit-costs-save"
            className="text-xs font-bold bg-[#0A0C10] text-white px-4 py-2 rounded-md hover:bg-black disabled:opacity-60 inline-flex items-center gap-1.5">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save costs
          </button>
        </div>
      </div>
      <div className="bg-white border border-black/8 rounded-xl overflow-hidden">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {Object.keys(costs).map((k) => (
            <div key={k} className="flex items-center justify-between gap-3 px-4 py-3 border-b border-r border-neutral-100">
              <div>
                <div className="text-sm font-medium">{COST_LABELS[k] || k}</div>
                <div className="text-[10px] font-mono text-neutral-400">default {defaults[k]}</div>
              </div>
              <input
                type="number" min="0" max="1000"
                value={costs[k]}
                onChange={(e) => setCosts({ ...costs, [k]: e.target.value === "" ? "" : Number(e.target.value) })}
                data-testid={`credit-cost-${k}`}
                className={`w-20 text-right px-2 py-1.5 border rounded-md text-sm font-mono outline-none focus:border-[#002FA7] ${costs[k] !== defaults[k] ? "border-[#FFB400] bg-[#FFB400]/5" : "border-neutral-200"}`}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
